/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Story read path (#240): list from `GET /api/disclosures`, load from the
 * capability-token read `GET /api/story/:storyId/:checksum`, then decrypt
 * client-side — DK looked up in the unlocked vault by `documentId`
 * (#247), AAD reconstructed from `(documentId, version)` per F13, and
 * the title recovered from the decrypted `{v, title, content}` envelope
 * (`document/storyplaintext.ts`).
 *
 * The literal-union story types (`disclosure | farewell`) and trigger types
 * here are the canonical frontend contract — `TdStoryForm` and the
 * disclosure pipeline (#166) both bind to these strings.
 */

import { z } from 'zod';
import { sha256 } from '@noble/hashes/sha2.js';
import { base64nopad } from '@scure/base';

import { clearElement } from './utils.js';
import { tdFetch } from './tdfetch.js';
import { vaultService } from './vault.js';
import {
  decryptDocumentContent,
  buildDocumentAad,
  DOCUMENT_NONCE_LENGTH_BYTES,
} from './document/encrypt.js';
import { decodeStoryPlaintext } from './document/storyplaintext.js';
import '../components/TdAddParticipant.js';
import '../components/TdStoryForm.js';

export type StoryType = 'disclosure' | 'farewell';

export type WhenType =
  'timeout' | 'custodian' | 'timeout-and-custodian' | 'timeout-or-custodian';

export type CheckInUnit = 'days' | 'weeks' | 'months';

export interface StoryParticipant {
  name: string;
  email: string;
}

/**
 * Summary row consumed by `TdStorylist` (#173, wired to the backend in
 * #240). The list-view fields are deliberately loose strings:
 *   - `type` is a display label (e.g. "Farewell", "Disclosure") derived
 *     from the backend `DisclosureType` enum.
 *   - `content` is the disclosure's cleartext `title` metadata (never
 *     the encrypted body) — a preview label, empty when absent.
 * `storyId`/`storyChecksum` carry the capability-token reference for
 * opening the underlying encrypted story; present only once the backend
 * persists them on the disclosure (app-backend#155).
 */
export interface StorySummary {
  id?: string;
  type?: string;
  content?: string;
  storyId?: string;
  storyChecksum?: string;
}

/**
 * Capability-token reference to an encrypted story. Knowledge of the
 * `(storyId, checksum)` pair *is* the read permission — the backend has
 * no per-story owner and serves the record only when the checksum
 * matches (see app-backend `StoryApp`).
 */
export interface StoryRef {
  storyId: string;
  checksum: string;
}

export interface StoryData {
  type: StoryType;
  title: string;
  content: string;
  participants: StoryParticipant[];
  whenType: WhenType;
  checkInValue: number;
  checkInUnit: CheckInUnit;
}

/**
 * Minimal structural type for the `<td-navigation>` element this module
 * touches. Cast at the call site so this file doesn't depend on the
 * TdNavigation source (still .js — separate sub-issue under #160 will
 * convert it).
 */
interface TdNavigationElement extends Element {
  addLayer(label: string): void;
  crumbs: string;
}

/**
 * Structural type for the `<td-story-form>` element. Mirrors the Lit
 * reactive properties declared in `TdStoryForm.js`. Cast at the
 * `createElement` call so this file doesn't depend on the TdStoryForm
 * source (also still .js).
 */
interface TdStoryFormElement extends HTMLElement {
  storyType: StoryType;
  title: string;
  content: string;
  whenType: WhenType;
  checkInValue: number;
  checkInUnit: CheckInUnit;
  participants: StoryParticipant[];
  setOriginButton(btn: HTMLButtonElement | null | undefined): void;
}

export const loadStory = async (
  target: HTMLElement,
  btn: HTMLButtonElement | null | undefined,
  storyRef?: StoryRef
): Promise<void> => {
  if (btn) {
    btn.disabled = true;
  }
  const storyData = await getStoryData(storyRef);
  console.log('Loading story', storyRef?.storyId);
  const tdNavigation = document.querySelector(
    'td-navigation'
  ) as TdNavigationElement | null;
  // #240: breadcrumb layer carries the decrypted story title; a fresh
  // (or untitled) story falls back to the "New Story" label.
  tdNavigation?.addLayer(storyData.title || 'New Story');

  clearElement(target);
  const storyForm = document.createElement(
    'td-story-form'
  ) as TdStoryFormElement;
  storyForm.storyType = storyData.type || 'disclosure';
  storyForm.title = storyData.title || '';
  storyForm.content = storyData.content || '';
  storyForm.whenType = storyData.whenType || 'timeout';
  storyForm.checkInValue = storyData.checkInValue || 4;
  storyForm.checkInUnit = storyData.checkInUnit || 'weeks';
  storyForm.participants = storyData.participants || [];
  storyForm.setOriginButton(btn);
  target.appendChild(storyForm);
};

/* -------------------------------------------------------------------------
 * Boundary schemas + typed error (#240)
 * ------------------------------------------------------------------------- */

/**
 * One row of `GET /api/disclosures`. Passthrough: the backend
 * `Disclosure` model carries more (participants, custodianIds, …) than
 * the list view needs; only the fields we render are validated.
 * `storyId`/`storyChecksum` are the story capability reference the
 * frontend sends since #240 (persisted server-side once
 * app-backend#155 lands).
 */
const DisclosureSummarySchema = z
  .object({
    id: z.string().min(1),
    type: z.enum(['FAREWELL', 'DISCLOSURE']).optional(),
    title: z.string().optional(),
    storyId: z.string().min(1).optional(),
    storyChecksum: z.string().min(1).optional(),
  })
  .passthrough();

const DisclosureListSchema = z.array(DisclosureSummarySchema);

/**
 * `GET /api/story/:storyId/:checksum` response. `content` is
 * base64nopad(nonce ‖ ciphertext) exactly as posted by the save flow;
 * `documentId`/`version`/`algorithmId` are the F13 AAD context the
 * backend persists opaquely (app-backend `Story` record).
 */
const StoryReadSchema = z
  .object({
    id: z.string().min(1),
    content: z.string().min(1),
    checksum: z.string().min(1),
    documentId: z.string().min(1),
    version: z.number().int().min(0),
    algorithmId: z.literal('XChaCha20-Poly1305'),
  })
  .passthrough();

/** Discriminator for `StoryLoadError` failure modes. */
export type StoryLoadFailureReason =
  | 'network-error' // fetch rejected (offline, connection refused)
  | 'request-rejected' // non-2xx from the server (wrong checksum ⇒ 404/403)
  | 'malformed-response' // body fails the boundary schema / base64 decode
  | 'checksum-mismatch' // content bytes don't hash to the declared checksum
  | 'vault-locked' // decryption needs the unlocked vault
  | 'missing-key' // no DK under this documentId in the vault
  | 'decrypt-failed'; // AEAD tag rejected (tamper / wrong key)

/**
 * Thrown by `getStoryList` / `getStoryData` on every failure path, with
 * a `reason` discriminator so the UI can render per-cause without
 * grepping message text (pattern per `RecoveryServiceError`).
 */
export class StoryLoadError extends Error {
  readonly reason: StoryLoadFailureReason;
  constructor(
    reason: StoryLoadFailureReason,
    message: string,
    options?: ErrorOptions
  ) {
    super(message, options);
    this.name = 'StoryLoadError';
    this.reason = reason;
  }
}

/** Fresh-story defaults — mirrors the fallbacks `loadStory` applies. */
const newStoryDefaults = (): StoryData => ({
  type: 'disclosure',
  title: '',
  content: '',
  participants: [],
  whenType: 'timeout',
  checkInValue: 4,
  checkInUnit: 'weeks',
});

const DISCLOSURE_TYPE_LABEL: Record<'FAREWELL' | 'DISCLOSURE', string> = {
  FAREWELL: 'Farewell',
  DISCLOSURE: 'Disclosure',
};

/**
 * Load a story for editing (#240).
 *
 * Without a `storyRef`, returns fresh-story defaults (the dashboard's
 * "new story" flow). With one, performs the capability-token read
 * `GET /api/story/:storyId/:checksum` and decrypts client-side:
 *
 *   1. checksum re-verified over the decoded content bytes,
 *   2. nonce split off (first 24 bytes), AAD rebuilt from
 *      `(documentId, version)` per F13,
 *   3. DK looked up in the unlocked vault by `documentId` (#247),
 *   4. plaintext decoded via the `{v, title, content}` envelope
 *      (raw-string fallback for pre-#240 stories).
 *
 * Trigger settings (`whenType`, check-in cadence) and participants live
 * on the *disclosure* record, not the story — they stay at defaults
 * here until the disclosure-edit flow lands.
 *
 * @throws {StoryLoadError} with a `reason` discriminator on every
 *   failure path (network, shape, checksum, locked vault, missing DK,
 *   AEAD rejection).
 */
export const getStoryData = async (storyRef?: StoryRef): Promise<StoryData> => {
  if (storyRef === undefined) {
    return newStoryDefaults();
  }

  let response: Response;
  try {
    response = await tdFetch(
      `/api/story/${encodeURIComponent(storyRef.storyId)}/${encodeURIComponent(storyRef.checksum)}`
    );
  } catch (cause) {
    throw new StoryLoadError('network-error', 'story request failed', {
      cause,
    });
  }
  if (!response.ok) {
    throw new StoryLoadError(
      'request-rejected',
      `story request rejected with HTTP ${response.status}`
    );
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch (cause) {
    throw new StoryLoadError(
      'malformed-response',
      'story response was not valid JSON',
      { cause }
    );
  }
  const parsed = StoryReadSchema.safeParse(json);
  if (!parsed.success) {
    throw new StoryLoadError(
      'malformed-response',
      `story response shape rejected: ${parsed.error.message}`,
      { cause: parsed.error }
    );
  }

  let combined: Uint8Array;
  try {
    combined = base64nopad.decode(parsed.data.content);
  } catch (cause) {
    throw new StoryLoadError(
      'malformed-response',
      'story content is not valid base64nopad',
      { cause }
    );
  }

  // Integrity: the declared checksum must match the content bytes —
  // the capability URL segment was already checked server-side, but a
  // proxy/storage corruption should fail loudly, not at AEAD depth.
  const localChecksum = base64nopad.encode(sha256(combined));
  if (localChecksum !== parsed.data.checksum) {
    throw new StoryLoadError(
      'checksum-mismatch',
      'story content does not match its declared checksum'
    );
  }

  if (combined.length <= DOCUMENT_NONCE_LENGTH_BYTES) {
    throw new StoryLoadError(
      'malformed-response',
      `story content is ${combined.length} bytes; expected nonce (${DOCUMENT_NONCE_LENGTH_BYTES}) + ciphertext`
    );
  }
  const nonce = combined.slice(0, DOCUMENT_NONCE_LENGTH_BYTES);
  const ciphertext = combined.slice(DOCUMENT_NONCE_LENGTH_BYTES);

  if (!vaultService.isUnlocked()) {
    throw new StoryLoadError(
      'vault-locked',
      'vault must be unlocked to decrypt a story'
    );
  }
  const dk = vaultService.retrieveSymmetricKey(parsed.data.documentId);
  if (dk === null) {
    throw new StoryLoadError(
      'missing-key',
      `no document key for documentId '${parsed.data.documentId}' in the vault`
    );
  }

  let plaintextBytes: Uint8Array;
  try {
    plaintextBytes = decryptDocumentContent({
      ciphertext,
      nonce,
      dk,
      aad: buildDocumentAad({
        documentId: parsed.data.documentId,
        version: parsed.data.version,
      }),
    });
  } catch (cause) {
    throw new StoryLoadError('decrypt-failed', 'story decryption failed', {
      cause,
    });
  } finally {
    dk.fill(0); // ephemeral copy (#247) — safe to zero after use
  }

  const { title, content } = decodeStoryPlaintext(
    new TextDecoder().decode(plaintextBytes)
  );
  return {
    ...newStoryDefaults(),
    title,
    content,
  };
};

/**
 * Fetch the dashboard list from `GET /api/disclosures` (#240) and map
 * each disclosure to the `StorySummary` shape `TdStorylist` renders.
 * Also resets the breadcrumb to `Dashboard` (list view is the root).
 *
 * @throws {StoryLoadError} on network failure, non-2xx response, or a
 *   response body that fails the boundary schema.
 */
export const getStoryList = async (): Promise<StorySummary[]> => {
  const tdNavigation = document.querySelector(
    'td-navigation'
  ) as TdNavigationElement | null;
  if (tdNavigation) {
    tdNavigation.crumbs = 'Dashboard';
  }

  let response: Response;
  try {
    response = await tdFetch('/api/disclosures');
  } catch (cause) {
    throw new StoryLoadError('network-error', 'disclosure list failed', {
      cause,
    });
  }
  if (!response.ok) {
    throw new StoryLoadError(
      'request-rejected',
      `disclosure list rejected with HTTP ${response.status}`
    );
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch (cause) {
    throw new StoryLoadError(
      'malformed-response',
      'disclosure list was not valid JSON',
      { cause }
    );
  }
  const parsed = DisclosureListSchema.safeParse(json);
  if (!parsed.success) {
    throw new StoryLoadError(
      'malformed-response',
      `disclosure list shape rejected: ${parsed.error.message}`,
      { cause: parsed.error }
    );
  }

  return parsed.data.map(d => ({
    id: d.id,
    type: d.type !== undefined ? DISCLOSURE_TYPE_LABEL[d.type] : 'Story',
    content: d.title ?? '',
    ...(d.storyId !== undefined && d.storyChecksum !== undefined
      ? { storyId: d.storyId, storyChecksum: d.storyChecksum }
      : {}),
  }));
};
